import React, { useContext, useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { cloneDeep } from 'lodash';
// import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TextField, Box, FormControl, InputLabel, Select,
  FormHelperText, Typography,
  Button, CircularProgress, makeStyles,
} from '@material-ui/core';
import { useAccount, useMsal } from '@azure/msal-react';
import Alert from '@material-ui/lab/Alert';
import InputMask from 'react-input-mask';
import validate from '../../helpers/validation';
import { fetchStaticToken } from '../../helpers/authentication';
import api from '../../config/api';
import AppContext from '../../state/AppContext';
import clientCategoryState, { clientTypeState } from '../../state';
import {
  selectedClinicIdState,
  selectedServiceIdState,
  selectedSubserviceIdState, selectedTimeSlotState, selectedTreatmentIdState,
} from '../../state/selected';
import { jwtTokenState, reservationState, validatedCustomerState } from '../../state/apiData';

const fieldKeys = [
  'firstName',
  'lastName',
  'birthdate',
  'sex',
  'email',
  'telephoneRes',
  'cell',
  'comments',
];

const complexFieldKeys = [
  'email',
];

const useStyles = makeStyles((theme) => ({
  notePhoneForm: {
    color: theme.palette.grey[600],
    marginTop: theme.spacing(1),
  },
}));

const SignupForm = () => {
  const intl = useIntl();
  const classes = useStyles();
  const {
    setIsAppointmentConfirmed,
    setCustomerNeedsSignup,
    isFollowUp,
  } = useContext(AppContext);

  const jwtToken = useRecoilValue(jwtTokenState);
  const selectedTreatmentId = useRecoilValue(selectedTreatmentIdState);
  const selectedTimeSlot = useRecoilValue(selectedTimeSlotState);
  const selectedSubserviceId = useRecoilValue(selectedSubserviceIdState);
  const selectedServiceId = useRecoilValue(selectedServiceIdState);
  const selectedClinicId = useRecoilValue(selectedClinicIdState);
  const clientType = useRecoilValue(clientTypeState);
  const clientCategory = useRecoilValue(clientCategoryState);
  const setValidatedCustomer = useSetRecoilState(validatedCustomerState);
  const [reservation, setReservation] = useRecoilState(reservationState);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(false);

  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {
    idTokenClaims: {
      emails: [],
    },
  });

  const formSpecsInitialState = {
    firstName: {
      mandatory: true,
      disabled: false,
      value: account.idTokenClaims.given_name || '',
      error: null,
    },
    lastName: {
      mandatory: true,
      disabled: false,
      value: account.idTokenClaims.family_name || '',
    },
    sex: {
      mandatory: true,
      disabled: false,
      value: '',
      error: null,
    },
    email: {
      mandatory: true,
      disabled: true,
      value: account.idTokenClaims.emails[0] || '',
      error: null,
      valid: false,
    },
    birthdate: {
      mandatory: true,
      disabled: false,
      value: '',
      error: null,
    },
    telephoneRes: {
      mandatory: false,
      disabled: false,
      displayValue: '',
      value: '',
      error: null,
    },
    cell: {
      mandatory: false,
      disabled: false,
      displayValue: '',
      value: '',
      error: null,
    },
    comments: {
      mandatory: true,
      disabled: false,
      value: '',
      error: null,
    },
  };

  const [formSpecs, setFormSpecs] = useState(() => formSpecsInitialState);

  const returnErrors = (errorKey, value) => {
    if (formSpecs[errorKey].mandatory) {
      if (fieldKeys.includes(errorKey)) {
        if (value.length < 1 || value === false) {
          return [{
            errorKey,
            errorVal: 'required',
          }];
        }
      }

      if (complexFieldKeys.includes(errorKey)) {
        if ((!validate(errorKey, value))) {
          return [{
            errorKey,
            errorVal: 'notValid',
          }];
        }
      }
    }

    // At least one phone number field needs to be filled
    if (errorKey === 'telephoneRes' || errorKey === 'cell') {
      const otherKey = errorKey === 'telephoneRes' ? 'cell' : 'telephoneRes';
      const valueOtherField = formSpecs[otherKey].value;

      if (value.length < 1 && valueOtherField.length < 1) {
        return [
          {
            errorKey: 'telephoneRes',
            errorVal: 'required',
          },
          {
            errorKey: 'cell',
            errorVal: null,
          },
        ];
      }

      return [
        {
          errorKey,
          errorVal: value.length > 0 && !validate('phone', value) ? 'notValid' : null,
        },
        {
          errorKey: otherKey,
          errorVal: valueOtherField.length > 0 && !validate('phone', valueOtherField) ? 'notValid' : null,
        },
      ];
    }

    return [{
      errorKey,
      errorVal: null,
    }];
  };

  const isFormValid = () => Object.keys(formSpecs)
    .filter((key) => formSpecs[key].error).length < 1;

  const updateFieldValue = (e) => {
    const updatedFormSpecs = JSON.parse(JSON.stringify(formSpecs));
    const {
      value,
      name,
    } = e.target;

    if (['telephoneRes', 'cell'].includes(name)) {
      updatedFormSpecs[name].displayValue = value;
      updatedFormSpecs[name].value = value.replace(/\D/g, '');
    } else {
      updatedFormSpecs[name].value = value;
    }

    returnErrors(name, updatedFormSpecs[name].value)
      .map((er) => {
        const {
          errorKey,
          errorVal,
        } = er;
        updatedFormSpecs[errorKey].error = errorVal;
        return null;
      });

    return setFormSpecs(updatedFormSpecs);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(false);
    setIsSubmitted(true);

    if (isFormValid()) {
      setIsSubmitting(true);

      const responseCustomer = await fetchStaticToken({
        url: api.customer,
        method: 'POST',
        body: {
          firstName: formSpecs.firstName.value,
          lastName: formSpecs.lastName.value,
          sex: parseInt(formSpecs.sex.value, 10),
          email: formSpecs.email.value,
          birthDate: dayjs(formSpecs.birthdate.value)
            .format('YYYY-MM-DD'),
          telephoneRes: formSpecs.telephoneRes.value,
          cell: formSpecs.cell.value,
          languageID: intl.locale === 'fr' ? 1 : 2,
          therapistID: selectedTimeSlot.employeeID,
          clinicID: selectedClinicId,
        },
      });

      if (responseCustomer.status === 200 || responseCustomer.status === 201) {
        const customerId = await responseCustomer.text();

        if (window && window.gtag) {
          window.gtag('event', 'select_content', {
            content_name: 'Confirm Appointment',
            content_lang: intl.locale,
          });
        }

        const responseBooking = await fetchStaticToken({
          url: api.appointment,
          method: 'POST',
          body: {
            customerID: customerId,
            therapistID: selectedTimeSlot.employeeID,
            disciplineID: selectedServiceId,
            subdisciplineID: selectedSubserviceId,
            treatmentID: selectedTreatmentId,
            clinicID: selectedClinicId,
            date: selectedTimeSlot.fromDayjs.format('YYYY-MM-DD'),
            time: selectedTimeSlot.fromDayjs.format('HH:mm:ss'),
            email: account.idTokenClaims.emails[0],
            comments: formSpecs.comments.value,
            jwt: jwtToken,
            reservationID: reservation ? reservation.id : null,
            client_type: clientType,
            client_category: clientCategory,
          },
        });

        if (responseBooking.status === 200 || responseBooking.status === 201) {
          setReservation(null);
          setIsAppointmentConfirmed(true);
        }

        setIsSubmitting(false);
        setValidatedCustomer({ customerID: customerId });
        return setCustomerNeedsSignup(false);
      }

      setError(true);
      setIsSubmitting(false);
    }

    return null;
  };

  useEffect(() => {
    setFormSpecs(fieldKeys.reduce((acc, key) => {
      const returnedFormSpecs = cloneDeep(acc);
      returnErrors(key, acc[key].value)
        .map((e) => {
          const {
            errorKey,
            errorVal,
          } = e;
          returnedFormSpecs[errorKey].error = errorVal;
          return null;
        });
      return returnedFormSpecs;
    }, formSpecs));
  }, []);

  return (
    <>
      <form onSubmit={onSubmit} noValidate>
        <Box mb={3}>
          <FormControl variant="outlined" style={{ width: '100%' }}>
            <InputLabel
              id="form__sex"
              error={isSubmitted && formSpecs.sex.error === 'required'}
              required={formSpecs.sex.mandatory}
            >
              <FormattedMessage id="SignupForm.sex" />
            </InputLabel>
            <Select
              name="sex"
              autoComplete="sex"
              label={<FormattedMessage id="SignupForm.sex" />}
              value={formSpecs.sex.value}
              onChange={updateFieldValue}
              error={isSubmitted && formSpecs.sex.error === 'required'}
              labelId="form__sex"
              native
              fullWidth
            >
              <option aria-label="None" value="" />
              <FormattedMessage id="Sex.female">
                {(message) => <option value="0">{message}</option>}
              </FormattedMessage>
              <FormattedMessage id="Sex.male">
                {(message) => <option value="1">{message}</option>}
              </FormattedMessage>
            </Select>
            {isSubmitted && formSpecs.sex.error === 'required' && (
              <FormHelperText error>
                <FormattedMessage
                  id="SignupForm.errorRequired"
                />
              </FormHelperText>
            )}
          </FormControl>
        </Box>
        <Box mb={3}>
          <TextField
            name="firstName"
            autoComplete="given-name"
            label={(<FormattedMessage id="SignupForm.firstName" />)}
            helperText={isSubmitted && formSpecs.firstName.error === 'required' && (
              <FormattedMessage id="SignupForm.errorRequired" />)}
            required={formSpecs.firstName.mandatory}
            disabled={formSpecs.firstName.disabled}
            value={formSpecs.firstName.value}
            onChange={updateFieldValue}
            error={isSubmitted && formSpecs.firstName.error === 'required'}
            variant="outlined"
            fullWidth
          />
        </Box>
        <Box mb={3}>
          <TextField
            name="lastName"
            autoComplete="family-name"
            label={(<FormattedMessage id="SignupForm.lastName" />)}
            helperText={isSubmitted && formSpecs.lastName.error === 'required' && (
              <FormattedMessage id="SignupForm.errorRequired" />)}
            required={formSpecs.lastName.mandatory}
            disabled={formSpecs.lastName.disabled}
            value={formSpecs.lastName.value}
            onChange={updateFieldValue}
            error={isSubmitted && formSpecs.lastName.error === 'required'}
            variant="outlined"
            fullWidth
          />
        </Box>
        <Box mb={3}>
          <TextField
            name="email"
            autoComplete="email"
            label={(<FormattedMessage id="SignupForm.email" />)}
            helperText={isSubmitted && formSpecs.email.error === 'required' && (
              <FormattedMessage id="SignupForm.errorRequired" />)}
            value={formSpecs.email.value}
            onChange={updateFieldValue}
            error={isSubmitted && formSpecs.email.error === 'required'}
            required={formSpecs.email.mandatory}
            disabled={formSpecs.email.disabled}
            type="email"
            variant="outlined"
            fullWidth
          />
        </Box>
        <Box mb={3}>
          <TextField
            name="birthdate"
            label={(<FormattedMessage id="SignupForm.birthdate" />)}
            helperText={isSubmitted && formSpecs.birthdate.error === 'required' && (
              <FormattedMessage id="SignupForm.errorRequired" />)}
            required={formSpecs.birthdate.mandatory}
            value={formSpecs.birthdate.value}
            onChange={updateFieldValue}
            error={isSubmitted && formSpecs.birthdate.error === 'required'}
            variant="outlined"
            fullWidth
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box mb={3}>
          <InputMask
            mask="999-999-9999"
            value={formSpecs.telephoneRes.displayValue}
            onChange={updateFieldValue}
            maskChar=""
          >
            {() => (
              <TextField
                name="telephoneRes"
                autoComplete="tel"
                label={(<FormattedMessage id="SignupForm.telephoneRes" />)}
                helperText={(
                  <>
                    {isSubmitted && formSpecs.telephoneRes.error === 'required' && (
                      <FormattedMessage id="SignupForm.errorRequiredPhone" />)}
                    {isSubmitted && formSpecs.telephoneRes.error === 'notValid' && (
                      <FormattedMessage id="SignupForm.errorNotValidPhone" />)}
                  </>
                )}
                required={formSpecs.telephoneRes.mandatory}
                error={isSubmitted && (formSpecs.telephoneRes.error === 'required' || formSpecs.telephoneRes.error === 'notValid')}
                variant="outlined"
                fullWidth
                type="tel"
              />
            )}
          </InputMask>
        </Box>
        <Box mb={3}>

          <InputMask
            mask="999-999-9999"
            value={formSpecs.cell.displayValue}
            onChange={updateFieldValue}
            maskChar=""
          >
            {() => (
              <TextField
                name="cell"
                autoComplete="tel"
                label={(<FormattedMessage id="SignupForm.cell" />)}
                helperText={isSubmitted && formSpecs.cell.error === 'notValid' && (
                  <FormattedMessage id="SignupForm.errorNotValidPhone" />)}
                required={formSpecs.cell.mandatory}
                onChange={updateFieldValue}
                error={isSubmitted && formSpecs.cell.error === 'notValid'}
                variant="outlined"
                type="tel"
                fullWidth
              />
            )}
          </InputMask>
          <Typography component="div" className={classes.notePhoneForm}>
            <FormattedMessage id="SignupForm.onePhoneAtLeast" />
          </Typography>
        </Box>
        {!isFollowUp
          && (
            <Box mb={3}>
              <TextField
                name="comments"
                label={(<FormattedMessage id="SignupForm.comments" />)}
                helperText={isSubmitted && formSpecs.comments.error === 'required' && (
                  <FormattedMessage id="SignupForm.errorRequired" />)}
                required={formSpecs.comments.mandatory}
                value={formSpecs.comments.value}
                onChange={updateFieldValue}
                error={isSubmitted && formSpecs.comments.error === 'required'}
                variant="outlined"
                multiline
                rows={4}
                fullWidth
                inputProps={{
                  maxLength: 250,
                }}
              />
            </Box>
          )}

        <Box mb={3}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? <CircularProgress size={20} />
              : (
                <FormattedMessage
                  id="SignupForm.submit"
                />
              )}
          </Button>
        </Box>
        {error
          && (
            <Alert severity="error">
              <FormattedMessage id="SignupForm.errorDefaultBackend" />
            </Alert>
          )}
      </form>
    </>
  );
};

SignupForm.propTypes = {};

export default SignupForm;
